import qs from "qs";
import { To, useNavigate, useSearchParams } from "react-router-dom";

import { uuid as uuidRegex } from "../../../utils/regex";
import {
  isNewCallPageQueryParam,
  isNewInPersonCallPageQueryParam,
} from "../../interview-assistant/pages/new-call/types";

/** Provides high-level filtering of keys to be included in URL query params */
export const getQueryString = (queryParams: Record<string, any>): string =>
  qs.stringify(queryParams, {
    filter: (prefix, value) => {
      if (prefix.startsWith("_")) return undefined;
      if (prefix === "isInPersonInterview") return value || undefined;
      if (prefix === "candidateId" && !uuidRegex.test(value)) return undefined;
      return value ?? undefined;
    },
    addQueryPrefix: true,
  });

/**
 * Shared navigation utilities for integration pages
 */
export const useIntegrationPageNavigation = (params?: {
  isInPersonInterview?: boolean;
}): {
  /** URL params from the current location */
  searchParams: URLSearchParams;
  /** Navigates with `replace` */
  navigate: (to: To) => void;
  /**
   * Navigates to the appropriate new call page, combining URL params
   * from the current location and any supplied values, and only
   * forwarding the params that are relevant
   */
  navigateToNewCallPage: (provided?: Record<string, any> | null) => void;
} => {
  const isInPersonInterview = !!params?.isInPersonInterview;

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  return {
    searchParams,
    navigate: (to) => navigate(to, { replace: true }),
    navigateToNewCallPage: (provided) => {
      const currentParams = Object.fromEntries(searchParams.entries());
      const mergedParams = {
        ...currentParams,
        ...provided,
        isInPersonInterview,
      };
      const filteredParams = Object.fromEntries(
        Object.entries(mergedParams).filter(([key]) =>
          isInPersonInterview
            ? isNewInPersonCallPageQueryParam(key)
            : isNewCallPageQueryParam(key)
        )
      );
      navigate(`/${getQueryString(filteredParams)}`, { replace: true });
    },
  };
};
